import React, {useRef, useCallback} from "react"
import {VariableSizeList as List, ListChildComponentProps} from "react-window"

const defaultRowHeight = 40
const defaultListWidth = 400
const defaultListHeight = 300

interface VirtualizedListProps<K> {
	items: [K, string][]
	height?: number
	width?: number | string
	renderRow: (key: K, value: string, ref?: (el: HTMLLabelElement | null) => void) => JSX.Element
}

/*
	VirtualizedList is a component that renders a list of items in a virtualized way.
	It means that only the items that are visible are rendered, and the rest are not.
	It helps to improve the performance of the list, especially when the list is large.
*/
export default function VirtualizedList<K>({
	items,
	height = defaultListHeight,
	width = defaultListWidth,
	renderRow,
}: VirtualizedListProps<K>) {
	const listRef = useRef<List>(null)
	const sizeMap = useRef<{[key: number]: number}>({})

	const getRowHeight = useCallback((index: number) => sizeMap.current[index] || defaultRowHeight, [])

	const setSize = useCallback((index: number, size: number) => {
		if (sizeMap.current[index] !== size) {
			sizeMap.current[index] = size
			listRef.current?.resetAfterIndex(index)
		}
	}, [])

	const Row = ({
		index,
		style,
		data,
	}: ListChildComponentProps<{
		items: [K, string][]
		setSize: (index: number, size: number) => void
	}>) => {
		const [key, value] = data.items[index]
		const rowRef = useCallback(
			(el: HTMLLabelElement | null) => {
				if (el) {
					const height = el.getBoundingClientRect().height
					data.setSize(index, height)
				}
			},
			[index, data]
		)

		return <div style={style}>{renderRow(key, value, rowRef)}</div>
	}

	return (
		<List
			ref={listRef}
			height={height}
			itemCount={items.length}
			itemSize={getRowHeight}
			width={width}
			itemData={{items, setSize}}
		>
			{Row}
		</List>
	)
}
